import { Flex } from 'components';
import colors from 'lib/colors';
import { useDebouncedValue } from 'lib/hooks';
import { cover } from 'polished';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { v4 as uuid } from 'uuid';

interface Props {
  children: React.ReactNode;
}

const LoadingContext = React.createContext<{
  loading: boolean;
  addLoading: (key: string, color: string, image?: string) => void;
  removeLoading: (key: string) => void;
}>({ loading: false, addLoading: (v) => {}, removeLoading: (v) => {} });

export const LOADING_IMG_1 = 'page-loading1.gif';
export const LOADING_IMG_2 = 'page-loading2.gif';

const LOADING_GIFS = [LOADING_IMG_1, LOADING_IMG_2];

const getRandomLoadingImage = () => {
  return LOADING_GIFS[Math.floor(Math.random() * LOADING_GIFS.length)];
};

interface LoaderOption {
  image?: string;
  withDelay?: number;
  color?: string;
}

export const useLoader = (loading: boolean, option?: LoaderOption) => {
  const { image, withDelay, color } = option || {};
  const bgColor = color || '';

  const {
    addLoading,
    removeLoading,
    loading: currentlyLoading,
  } = useContext(LoadingContext);

  const [key] = useState(uuid());

  let startAnimationTimeout = useRef<ReturnType<typeof setTimeout>>();
  let stopAnimationTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const startDelayedAnimation = (loadingFunction: () => void) => {
      clearTimeout(stopAnimationTimeout.current);

      startAnimationTimeout.current = setTimeout(() => {
        if (loading) {
          loadingFunction();
        }
      }, withDelay || 0);
    };

    const stopDelayedAnimation = (loadingFunction: () => void) => {
      stopAnimationTimeout.current = setTimeout(() => {
        loadingFunction();
      }, 250);

      clearTimeout(startAnimationTimeout.current);
    };

    if (loading) {
      withDelay
        ? startDelayedAnimation(() => addLoading(key, bgColor, image))
        : addLoading(key, bgColor, image);
    } else {
      withDelay
        ? stopDelayedAnimation(() => removeLoading(key))
        : removeLoading(key);

      clearTimeout(stopAnimationTimeout.current);
    }

    return () => {
      withDelay
        ? stopDelayedAnimation(() => removeLoading(key))
        : removeLoading(key);
    };
  }, [key, addLoading, removeLoading, loading, image, bgColor, withDelay]);

  return currentlyLoading;
};

const LoadingProvider = ({ children }: Props) => {
  const [loading, setLoading] = useState<string[]>([]);
  const [image, setImage] = useState<string>(getRandomLoadingImage());
  const [bgColor, setBgColor] = useState<string>('');

  const handleAddLoading = useCallback(
    (key: string, color: string, image?: string) => {
      setLoading((l) => [...l, key]);
      setBgColor(color);
      if (image) {
        setImage(image);
      }
    },
    [setLoading, setImage],
  );

  const handleRemoveLoading = useCallback(
    (key: string) => {
      setLoading((l) => l.filter((v) => v !== key));
    },
    [setLoading],
  );

  useEffect(() => setImage(getRandomLoadingImage()), [setImage]);

  // To prevent bliping loading animation on and off if its too fast
  const showLoadingOverlay = useDebouncedValue(!!loading.length, 250);
  return (
    <LoadingContext.Provider
      value={{
        loading: !!loading.length,
        addLoading: handleAddLoading,
        removeLoading: handleRemoveLoading,
      }}
    >
      {showLoadingOverlay && (
        <Flex
          css={{
            ...cover(),
            position: 'fixed',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: bgColor || colors.blueBackground,
            zIndex: 10000,
          }}
        >
          <img src={require(`../../assets/${image}`).default} alt="Loading" />
        </Flex>
      )}
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingProvider;
